import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { PagedData, Summary } from '../../lib/types.js';
import { fmtInt, rangeFor } from '../../lib/format.js';
import { accountOption, sourceKeyName, sourceName } from '../../lib/source-name.js';
import { RANGES } from '../../lib/ranges.js';
import { dailyQuery, dailySearch, parseDailyUrl, type DailyUrlState } from '../../lib/daily-url.js';
import type { PageFilters } from '../../lib/page-filters.js';
import { FilterSelect } from '../../components/FilterSelect.js';
import { useIsPhone } from '../../lib/use-media.js';
import { errorMessage } from '../settings/common.js';
import { DailyTable } from './DailyTable.js';

/**
 * Daily: the Daily breakdown table, exactly as the Dashboard had it - the daily
 * stats per account, feed and geo, grouped by day - on its own page, with the
 * Dashboard's date range and filters. The range and filters live in the
 * address so a view can be bookmarked. (A net-by-day-and-account grid sat
 * above the table from 87b2e54 to 73648f8; it was taken off for now - the
 * server read behind it, GET /api/daily, is still there.)
 */
const PATH = '/daily';

export function DailyPage() {
  const phone = useIsPhone();
  const [state, setStateRaw] = useState<DailyUrlState>(() => parseDailyUrl(window.location.search));
  useEffect(() => {
    const resync = () => setStateRaw(parseDailyUrl(window.location.search));
    window.addEventListener('popstate', resync);
    return () => window.removeEventListener('popstate', resync);
  }, []);
  const setState = (next: DailyUrlState) => {
    setStateRaw(next);
    // Replace, not push: a filter click is not a page the Back button should walk through.
    window.history.replaceState(null, '', `${PATH}${dailySearch(next)}`);
  };
  const { days, filters } = state;
  const setFilter = (k: keyof PageFilters, v: string | undefined) =>
    setState({ days, filters: { ...filters, [k]: v } });

  const [hideEmpty, setHideEmpty] = useState(true);
  const [tableLimit, setTableLimit] = useState(500);
  const [tableOpen, setTableOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const segRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!phone) return;
    segRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [phone, days]);

  const { from, to } = rangeFor(days);
  const qs = dailyQuery(from, to, filters);
  const table = useQuery({
    queryKey: ['data', qs, tableLimit],
    queryFn: () => api.get<PagedData>(`/api/data?${qs}&limit=${tableLimit}`),
  });
  // The filter options, each day's whole-day attributed cost and the partial day: the Dashboard's summary.
  const summary = useQuery({
    queryKey: ['summary', qs],
    queryFn: () => api.get<Summary>(`/api/summary?${qs}`),
  });
  const s = summary.data;

  const accountsById = useMemo(() => new Map((s?.accounts ?? []).map((a) => [a.id, a])), [s]);
  const provDisp = (key: string, label?: string | null) => sourceKeyName(key, label, accountsById);
  const labelById = useMemo(() => new Map((s?.accounts ?? []).map((a) => [a.id, a.label])), [s]);
  const rowSource = (r: { accountId: string; provider: string; providerLabel?: string | null | undefined }) =>
    sourceName({ provider: r.provider, providerLabel: r.providerLabel, label: labelById.get(r.accountId) });

  const activeFilters = (Object.entries(filters) as [keyof PageFilters, string | undefined][]).filter(([, v]) => !!v);
  const chipLabel = (k: keyof PageFilters, v: string): string => {
    if (k === 'account') {
      const a = s?.accounts.find((x) => x.id === v);
      return a ? accountOption(a) : v;
    }
    if (k === 'feedId') return `Feed ${v}`;
    if (k === 'provider') return provDisp(v);
    return v;
  };

  return (
    <div>
      <div className="pagehead">
        <h1>Daily</h1>
        <div className="head-right">
          <div className="meta">
            {from} → {to}
          </div>
        </div>
      </div>

      <div className="filters">
        <div className="seg" role="group" aria-label="Date range" ref={segRef}>
          {RANGES.map((r) => (
            <button key={r.days} aria-pressed={days === r.days} onClick={() => setState({ days: r.days, filters })}>
              {r.label}
            </button>
          ))}
        </div>
        {phone ? (
          <>
            <button
              type="button"
              className="chip"
              aria-expanded={filtersOpen}
              aria-controls="daily-filters-panel"
              onClick={() => setFiltersOpen((v) => !v)}
            >
              Filters{activeFilters.length ? ` · ${activeFilters.length}` : ''}
            </button>
            {!filtersOpen
              ? activeFilters.map(([k, v]) => (
                  <button key={k} type="button" className="chip active-filter" title="Clear this filter" onClick={() => setFilter(k, undefined)}>
                    {chipLabel(k, v ?? '')} ✕
                  </button>
                ))
              : null}
          </>
        ) : null}
        {!phone || filtersOpen ? (
          <div className="filter-group" id="daily-filters-panel">
            <FilterSelect
              label="All accounts"
              value={filters.account}
              options={(s?.accounts ?? []).map((a) => ({ value: a.id, label: accountOption(a) }))}
              onChange={(v) => setFilter('account', v)}
            />
            <FilterSelect
              label="All reps"
              value={filters.rep}
              options={(s?.filterOptions.reps ?? []).map((v) => ({ value: v, label: v }))}
              onChange={(v) => setFilter('rep', v)}
            />
            <FilterSelect
              label="All sources"
              value={filters.provider}
              options={(s?.filterOptions.providers ?? []).map((v) => ({ value: v, label: provDisp(v) }))}
              onChange={(v) => setFilter('provider', v)}
            />
            <FilterSelect
              label="All feeds"
              value={filters.feedId}
              options={(s?.filterOptions.feeds ?? []).map((v) => ({ value: v, label: `Feed ${v}` }))}
              onChange={(v) => setFilter('feedId', v)}
            />
            <FilterSelect
              label="All countries"
              value={filters.country}
              options={(s?.filterOptions.countries ?? []).map((v) => ({ value: v, label: v }))}
              onChange={(v) => setFilter('country', v)}
            />
            <button
              className="chip"
              aria-pressed={hideEmpty}
              title="Display only: hides rows with no searches, revenue or clicks. No money changes with it."
              onClick={() => setHideEmpty(!hideEmpty)}
            >
              Hide request-only
            </button>
          </div>
        ) : null}
      </div>

      {table.isError ? (
        <div className="table-card">
          <p className="ui-field__error">{errorMessage(table.error)}</p>
          <button className="ghost small" onClick={() => void table.refetch()}>
            Retry
          </button>
        </div>
      ) : phone && !tableOpen ? (
        <button type="button" className="ghost wide-btn" onClick={() => setTableOpen(true)}>
          Show the daily breakdown{table.data ? ` (${fmtInt(table.data.total)} rows)` : ''}
        </button>
      ) : (
        <DailyTable
          rows={table.data?.rows ?? []}
          total={table.data?.total ?? 0}
          hideEmpty={hideEmpty}
          showCosts
          daily={s?.daily ?? []}
          partialDay={s?.partialDay ?? null}
          csvHref={`/api/data.csv?${qs}`}
          provDisp={provDisp}
          rowSource={rowSource}
          onMore={() => setTableLimit((l) => l + 500)}
        />
      )}
    </div>
  );
}
